/**
 * @file Shared Axios HTTP client: credentialed cookies, CSRF double-submit header,
 * network/gateway retries, and silent session refresh on 401.
 * @module frontend/src/api/client
 */

import axios from 'axios';

// Use relative URL to leverage React dev server proxy
// The proxy in package.json forwards /api/* to http://localhost:3001
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
});

/**
 * Read a browser cookie by name (used for readable csrf_token double-submit).
 * @param {string} name
 * @returns {string|null}
 */
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// Helper function to check if an error is a network error that should be retried
const isRetryableNetworkError = (error) => {
  // Network errors that can be retried
  const networkErrorCodes = [
    'ERR_NETWORK',
    'ERR_NETWORK_CHANGED',
    'ERR_INTERNET_DISCONNECTED',
    'ECONNABORTED', // Timeout
    'ETIMEDOUT',
  ];

  // Check if error code matches retryable network errors
  if (error.code && networkErrorCodes.some(code => error.code.includes(code))) {
    return true;
  }

  // Check error message for network-related keywords
  const errorMessage = (error.message || '').toLowerCase();
  const networkKeywords = [
    'network',
    'connection',
    'timeout',
    'disconnect',
    'failed to fetch',
    'networkerror',
  ];

  if (networkKeywords.some(keyword => errorMessage.includes(keyword))) {
    return true;
  }

  // Check if no response was received (network issue)
  if (!error.response && error.request) {
    return true;
  }

  return false;
};

/** Brief nginx/Railway upstream blips — safe to retry idempotent-ish client calls */
const isRetryableGatewayStatus = (status) =>
  status === 502 || status === 503 || status === 504;

/**
 * True when 502/503/504 came from the proxy (HTML/empty), not from Express JSON.
 * App-level 502s (e.g. email send failure) must not be retried.
 */
const isProxyGatewayBlip = (error) => {
  const status = error.response?.status;
  if (!isRetryableGatewayStatus(status)) return false;
  const data = error.response?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return false;
  }
  return true;
};

const isRetryableRequestError = (error) =>
  isRetryableNetworkError(error) || isProxyGatewayBlip(error);

// Helper function to wait (for exponential backoff)
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 500; // 500ms initial delay
const SILENT_REFRESH_ATTEMPTS = 3;

/** Shared in-flight refresh so concurrent 401s wait on one /auth/refresh */
let refreshPromise = null;

/** SPA registers this so hard auth failure clears React state (soft redirect via ProtectedRoute). */
let authExpiredHandler = null;

/**
 * Register a callback invoked when silent refresh fails with hard auth (401/403).
 * @param {(() => void)|null} handler
 */
export function setAuthExpiredHandler(handler) {
  authExpiredHandler = typeof handler === 'function' ? handler : null;
}

function notifyAuthExpired() {
  if (authExpiredHandler) {
    try {
      authExpiredHandler();
    } catch {
      /* ignore handler errors */
    }
  }
}

function isAuthEndpointUrl(url = '') {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/me') ||
    url.includes('/auth/forgot') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/config')
  );
}

/** Session bootstrap — fail fast so a down API does not stall "Checking session..." */
function isAuthBootstrapUrl(url = '') {
  return (
    url.includes('/auth/refresh') ||
    url.includes('/auth/me') ||
    url.includes('/auth/config')
  );
}

/**
 * Refresh access cookie. Distinguishes hard auth failure from transient outages
 * so we do not bounce users to login on a brief 502, CSRF mismatch, or rate limit.
 * Only a 401 from /auth/refresh is treated as a dead session.
 * @returns {Promise<'ok' | 'auth' | 'transient'>}
 */
async function silentRefresh() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      for (let attempt = 1; attempt <= SILENT_REFRESH_ATTEMPTS; attempt++) {
        try {
          await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...(getCookie('csrf_token')
                  ? { 'X-CSRF-Token': getCookie('csrf_token') }
                  : {}),
              },
              timeout: 15000,
            }
          );
          return 'ok';
        } catch (err) {
          const status = err.response?.status;
          if (status === 401) {
            return 'auth';
          }
          // 403 (CSRF) and 429 are retryable — not a dead session.
          const retryable =
            isRetryableRequestError(err) || status === 403 || status === 429;
          if (!retryable || attempt === SILENT_REFRESH_ATTEMPTS) {
            return 'transient';
          }
          await wait(INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1));
        }
      }
      return 'transient';
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Keep the access cookie alive while the tab is open (default JWT is 15m).
 * @returns {Promise<'ok' | 'auth' | 'transient'>}
 */
export async function ensureFreshSession() {
  return silentRefresh();
}

// Request interceptor: retries + CSRF double-submit header
client.interceptors.request.use(
  (config) => {
    if (!config.__retryCount) {
      config.__retryCount = 0;
    }
    const csrf = getCookie('csrf_token');
    // REVIEW [MEDIUM]: Mutating requests proceed without X-CSRF-Token when csrf_token
    // cookie is absent — confirm backend rejects those instead of silently accepting.
    if (csrf) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrf;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with retry logic
client.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    // REVIEW [LOW]: Console-logs auction-groups response bodies — avoid in production builds.
    if (
      process.env.NODE_ENV !== 'production' &&
      response.config.url?.includes('auction-groups')
    ) {
      console.log('API Response:', {
        url: response.config.url,
        method: response.config.method,
        status: response.status,
        data: response.data,
      });
    }
    return response;
  },
  async (error) => {
    const config = error.config;

    // Check if this is a retryable network/gateway error and we haven't exceeded max retries
    // Long uploads (merge) set skipNetworkRetry so a late response is not re-sent.
    // Auth bootstrap must fail fast — retries turn a down API into a multi-minute
    // "Checking session..." spinner (30s timeout × 4 attempts × several calls).
    // User-initiated auth (login, register, forgot-password) is retried: a brief
    // nginx 502 should not require a manual second click.
    if (
      isRetryableRequestError(error) &&
      config &&
      !config.skipNetworkRetry &&
      !isAuthBootstrapUrl(config.url || '') &&
      config.__retryCount < MAX_RETRIES
    ) {
      config.__retryCount = (config.__retryCount || 0) + 1;

      // Calculate delay with exponential backoff
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, config.__retryCount - 1);

      console.warn(`Transient error detected, retrying (${config.__retryCount}/${MAX_RETRIES}) after ${delay}ms:`, {
        url: config.url,
        status: error.response?.status,
        error: error.code || error.message,
      });

      // Wait before retrying
      await wait(delay);

      // Retry the request
      try {
        return await client(config);
      } catch (retryError) {
        // If retry also fails, continue to error handling below
        error = retryError;
      }
    }

    // Log error (only after all retries exhausted or non-retryable error)
    if (!config || config.__retryCount >= MAX_RETRIES) {
      console.error('API Error:', {
        url: config?.url,
        method: config?.method,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        retries: config?.__retryCount || 0,
      });
    }

    // Create enhanced error message
    const rawError = error.response?.data?.error;
    let message =
      (typeof rawError === 'string' ? rawError : rawError?.message) ||
      error.message ||
      'An error occurred';
    const statusCode = error.response?.status;

    // Access expired — try silent refresh once, then retry. Only a 401 from
    // refresh clears the SPA session; transient failures leave the user signed in.
    const url = config?.url || '';
    if (
      statusCode === 401 &&
      config &&
      !config.__retriedAfterRefresh &&
      !isAuthEndpointUrl(url)
    ) {
      config.__retriedAfterRefresh = true;
      const refreshResult = await silentRefresh();
      if (refreshResult === 'ok') {
        const csrf = getCookie('csrf_token');
        if (csrf) {
          config.headers = config.headers || {};
          config.headers['X-CSRF-Token'] = csrf;
        }
        return client(config);
      }
      // Brief API outage during refresh — do not force logout; let the caller retry
      if (refreshResult === 'transient') {
        const transientError = new Error(
          'The server was temporarily unavailable. Please try again in a moment.'
        );
        transientError.statusCode = 502;
        transientError.isNetworkError = true;
        transientError.response = error.response;
        return Promise.reject(transientError);
      }
      // Hard auth failure — clear SPA session; ProtectedRoute soft-navigates to login
      notifyAuthExpired();
    }

    // Improve error message for network / gateway errors
    if (isRetryableRequestError(error)) {
      if (isRetryableGatewayStatus(statusCode)) {
        message =
          'The server was temporarily unavailable (bad gateway). Please try again in a moment.';
      } else if (error.code === 'ERR_NETWORK_CHANGED') {
        message = 'Network connection changed. Please check your internet connection and try again.';
      } else if (error.code === 'ERR_INTERNET_DISCONNECTED' || error.message?.toLowerCase().includes('internet')) {
        message = 'No internet connection. Please check your network and try again.';
      } else if (error.code === 'ECONNABORTED' || error.code?.includes('TIMEOUT')) {
        message = 'Request timed out. The server may be slow or your connection is unstable.';
      } else {
        message = 'Network error occurred. Please check your connection and try again.';
      }
    }

    // Blob error responses often lack a JSON body — surface status clearly
    if (
      statusCode &&
      config?.responseType === 'blob' &&
      (!rawError || typeof rawError !== 'string')
    ) {
      if (isRetryableGatewayStatus(statusCode)) {
        message =
          'The server was temporarily unavailable while generating the file. Please try again.';
      } else if (statusCode === 401) {
        message = 'Authentication required. Please refresh the page and try again.';
      }
    }

    const enhancedError = new Error(message);
    if (statusCode) {
      enhancedError.statusCode = statusCode;
      enhancedError.originalMessage = error.message;
    }
    enhancedError.response = error.response;
    enhancedError.isNetworkError = isRetryableRequestError(error);
    enhancedError.retryCount = config?.__retryCount || 0;

    return Promise.reject(enhancedError);
  }
);

export default client;
